(() => {
  'use strict';

  const dataEl = document.getElementById('subscribe-best-results-data');
  if (!dataEl) return;

  const { mappings } = JSON.parse(dataEl.textContent);

  const parsedMappings = mappings
    .filter((m) => m.popup_image)
    .map((m) => ({
      keywords: m.keywords.split(',').map((k) => k.trim().toLowerCase()).filter(Boolean),
      bundleUrls: m.bundle_urls.split(',').map((u) => u.trim()).filter(Boolean),
      img: m.popup_image,
      label: (m.label || '').trim(),
    }));

  let currentImgSrc = null;
  let currentLabel = null;
  let lastTrigger = null;

  // ─── Image resolution ───────────────────────────────────────────────────────

  const resolveMatch = (title) => {
    const t = title.toLowerCase();
    return parsedMappings.find(({ keywords }) => keywords.some((kw) => t.includes(kw))) ?? null;
  };

  const resolveMatchByBundle = (href) => {
    return parsedMappings.find(({ bundleUrls }) => bundleUrls.some((u) => href.includes(u))) ?? null;
  };

  // ─── Dialog helpers ─────────────────────────────────────────────────────────

  const getPopup = () => document.querySelector('[data-best-results-popup]');

  const getFocusable = (container) =>
    [...container.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )].filter((el) => getComputedStyle(el).display !== 'none');

  // ─── Background inert (WCAG: APG Dialog Pattern) ────────────────────────────
  // Prevents keyboard and AT from reaching background content while dialog is open.
  // Uses the `inert` attribute (universally supported as of 2024+).

  const setBackgroundInert = (makeInert) => {
    const popup = getPopup();
    if (!popup) return;
    // Inert all top-level body children that don't contain the popup (header, other sections, footer)
    Array.from(document.body.children).forEach((child) => {
      if (!child.contains(popup)) child.inert = makeInert;
    });
    // Inert the cart content wrapper — sibling of popup inside the same section
    const cartWrap = document.querySelector('.container.content.cart-wrap');
    if (cartWrap) cartWrap.inert = makeInert;
  };

  // ─── Focus trap (WCAG 2.4.3, APG Dialog Pattern) ────────────────────────────
  // Tab and Shift+Tab cycle within the dialog; focus cannot escape.

  const onTrapKeydown = (e) => {
    if (e.key !== 'Tab') return;
    const popup = getPopup();
    if (!popup) return;
    const focusable = getFocusable(popup);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  // ─── Open / Close ────────────────────────────────────────────────────────────

  const openPopup = (triggerEl) => {
    const popup = getPopup();
    if (!popup || !currentImgSrc) return;

    // Track trigger so focus can return on close (WCAG 2.4.3)
    lastTrigger = triggerEl ?? null;

    // Set image content + meaningful alt text (WCAG 1.1.1)
    const img = popup.querySelector('.subscribe-best-results-popup__image');
    if (img) {
      img.src = currentImgSrc;
      img.alt = currentLabel ? `Results timeline for ${currentLabel}` : '';
    }

    // Update dialog label to reflect current product context (WCAG 4.1.2)
    popup.setAttribute(
      'aria-label',
      currentLabel ? `${currentLabel} — results timeline` : 'Product results'
    );

    popup.classList.add('popup-active');
    setBackgroundInert(true);
    document.addEventListener('keydown', onTrapKeydown);

    // Move focus into dialog (APG Dialog Pattern)
    const focusable = getFocusable(popup);
    if (focusable.length) focusable[0].focus();
  };

  const closePopup = () => {
    const popup = getPopup();
    if (!popup) return;
    popup.classList.remove('popup-active');
    document.removeEventListener('keydown', onTrapKeydown);
    setBackgroundInert(false);

    // Return focus to the element that opened the dialog (APG Dialog Pattern)
    if (lastTrigger) {
      lastTrigger.focus();
      lastTrigger = null;
    }
  };

  // ─── Events ─────────────────────────────────────────────────────────────────

  // Escape key closes dialog (APG Dialog Pattern)
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closePopup();
  });

  document.addEventListener('click', (e) => {
    // Open: click anywhere on widget — trigger is always the button for focus return
    const widget = e.target.closest('.subscribe-best-results');
    if (widget) {
      e.preventDefault();
      openPopup(widget.querySelector('.subscribe-best-results__button'));
      return;
    }

    // Close: close button
    if (e.target.closest('.subscribe-best-results-popup__close-button')) {
      closePopup();
      return;
    }

    // Close: click on overlay backdrop (outside wrapper)
    const popup = getPopup();
    if (popup && e.target === popup) closePopup();
  });

  // ─── Cart state → widget visibility ────────────────────────────────────────

  const updateWidgets = () => {
    const itemsEl = document.querySelector('.cart-container__cart-page .js-cart-items');
    if (!itemsEl) return;

    const items = itemsEl.querySelectorAll('.cart__item');
    let match = null;

    if (items.length === 1) {
      const imageEl = items[0].querySelector('.one-third.cart__item--image a[title]');
      const title = imageEl?.getAttribute('title') ?? '';
      match = resolveMatch(title);

      if (!match) {
        const bundleAnchor = items[0].querySelector('.product-bundle-card__image-wrapper');
        const href = bundleAnchor?.getAttribute('href') ?? '';
        if (href) match = resolveMatchByBundle(href);
      }
    }

    currentImgSrc = match?.img ?? null;
    currentLabel = match?.label ?? null;

    const widgets = document.querySelectorAll('[data-best-results-widget]');
    if (currentImgSrc) {
      widgets.forEach((el) => el.classList.add('subscribe-best-results--visible'));
    } else {
      widgets.forEach((el) => el.classList.remove('subscribe-best-results--visible'));
      closePopup();
    }
  };

  // ─── MutationObserver ───────────────────────────────────────────────────────

  const observer = new MutationObserver(() => {
    observer.disconnect();
    updateWidgets();
    observer.observe(document.body, { childList: true, subtree: true });
  });

  const init = () => {
    updateWidgets();
    observer.observe(document.body, { childList: true, subtree: true });
  };

  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', init)
    : init();
})();
